import React, { useEffect, useState } from "react";
import { useDeleteOne, useGetHouse, useUploadOne } from "../stores/houseStore";

const HouseGallery = ({ house, onUpdate }) => {
    const [selected, setSelected] = useState(house?.media?.[0] || null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false); // остаётся для загрузки файла
    const [initialLoading, setInitialLoading] = useState(house == null || house.media == null);
    const [imageLoaded, setImageLoaded] = useState(false); // для fade-in выбранного изображения
    const token = localStorage.getItem("access_token");
    const [error, setError] = useState(null);

    const getHouseById = useGetHouse();
    const deleteOne = useDeleteOne();
    const uploadOne = useUploadOne();
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    // следим за приходом house.media — выключаем initialLoading и ставим selected
    useEffect(() => {
        const mediaAbsent = house == null || house.media == null;
        setInitialLoading(mediaAbsent);
        if (!mediaAbsent) {
            setSelected(house.media?.[0] || null);
        }
    }, [house]);

    // когда selected меняется — сбрасываем флаг загрузки картинки
    useEffect(() => {
        setImageLoaded(false);
    }, [selected?.url]);

    const handleDelete = async (id) => {
        try {
            await deleteOne(id, token);
            const updated = await getHouseById(house.id, token);
            onUpdate?.(updated);
            setSelected(null);
        } catch (err) {
            setError(err.message || "Ошибка удаления");
            console.error(err);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        setError(null);

        try {
            await uploadOne(house.id, file, token);
            const updated = await getHouseById(house.id, token);
            onUpdate?.(updated);
        } catch (err) {
            setError(err.message || "Ошибка загрузки");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center space-y-4">
            <div className="w-full max-w-3xl">
                {/* 1) начальный skeleton, 2) выбранное изображение (с overlay skeleton до onLoad), 3) заглушка если нет картинок */}
                {initialLoading ? (
                    <div className="w-full h-[500px] rounded-xl bg-gray-300 animate-pulse" />
                ) : selected ? (
                    <div className="relative">
                        {/* skeleton overlay пока <img> не загрузилась */}
                        {!imageLoaded && (
                            <div className="absolute inset-0 rounded-xl bg-gray-300 animate-pulse z-10" />
                        )}

                        <img
                            src={selected.url}
                            alt={`${house.title} — фото ${house.media?.indexOf(selected) + 1 || ''}`}
                            onLoad={() => setImageLoaded(true)}
                            className="w-full h-[500px] object-cover rounded-xl shadow-lg transition-opacity duration-500"
                            style={{ opacity: imageLoaded ? 1 : 0 }}
                        />

                        {house.owner_id === user.id && (
                            <button
                                onClick={() => setShowConfirm(true)}
                                className="absolute top-4 right-4 z-20"
                            >
                                <svg
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M6 6L18 18M6 18L18 6"
                                        stroke={"#FF0000"}
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        )}

                        {showConfirm && (
                            <div className="fixed inset-0 flex items-start justify-center py-20 bg-black/50 z-50">
                                <div className="bg-gray-300 rounded-lg shadow-lg p-6 w-[300px]">
                                    <h2 className="text-lg font-semibold text-gray-800 mb-4">
                                        Вы уверены, что хотите удалить медиафайл?
                                    </h2>
                                    <div className="flex justify-end gap-3">
                                        <button
                                            onClick={() => setShowConfirm(false)}
                                            className="px-4 py-2 bg-gray-400 rounded-lg hover:bg-gray-500"
                                        >
                                            Отмена
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleDelete(selected.id);
                                                setShowConfirm(false);
                                            }}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                        >
                                            Удалить
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="w-full h-[500px] rounded-xl bg-gray-200 flex items-center justify-center text-gray-500">
                        Нет изображений
                    </div>
                )}
            </div>

            {/* Thumbnails / skeletons */}
            <div className="flex items-center space-x-3 overflow-x-auto">
                {initialLoading ? (
                    // несколько скелетонов для миниатюр при первой загрузке
                    Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="w-24 h-20 bg-gray-300 animate-pulse rounded-lg" />
                    ))
                ) : (
                    (house.media || []).map((img, index) => (
                        <img
                            key={img.id ?? index}
                            src={img.url}
                            alt={`thumb-${index}`}
                            className={`w-24 h-20 object-cover rounded-lg cursor-pointer border-2 transition ${
                                selected === img ? "border-green-500" : "border-transparent"
                            }`}
                            onClick={() => setSelected(img)}
                        />
                    ))
                )}

                {house?.owner_id === user.id && (
                    <label className="w-24 h-20 flex items-center justify-center bg-white/30 backdrop-blur-md rounded-lg cursor-pointer relative">
                        {loading ? (
                            // индикатор загрузки файла
                            <div className="w-full h-full flex items-center justify-center">
                                <div className="w-6 h-6 border-2 border-gray-400 rounded-full border-t-transparent animate-spin" />
                            </div>
                        ) : (
                            "+"
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                    </label>
                )}
            </div>

            {error && <p className="text-red-500">{error}</p>}
        </div>
    );
};

export default HouseGallery;
